import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

const DefaultContent = () => (
  <div className="p-6">
    <h2 className="text-xl font-semibold mb-4">Panel de contenido</h2>
    <p>Este es el contenido por defecto del panel lateral.</p>
  </div>
);

const MultiSidePanel = ({
  isOpen = false,
  onClose,
  content = "default",
  width = "420px",
  title = "Panel",
  contentProps = {},
  position = "right"
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Esperamos un frame para permitir que el DOM se actualice
      // antes de iniciar la animación
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      
      // Prevent body scrolling when panel is open
      document.body.style.overflow = "hidden";
    } else {
      setIsAnimating(false);
      // Esperamos a que termine la animación antes de ocultar
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      // Restore body scrolling when panel is closed
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Don't render anything if the panel is not visible
  if (!isVisible) return null;

  const contentMap = {
    default: <DefaultContent />,
  };

  const contentToRender = typeof content === 'string' 
    ? (contentMap[content] || contentMap.default)
    : content;

  const positionClasses = {
    right: "right-0",
    left: "left-0"
  };

  const transformClasses = {
    right: isAnimating ? "translate-x-0" : "translate-x-full",
    left: isAnimating ? "translate-x-0" : "-translate-x-full"
  };

  const positionClass = positionClasses[position] || positionClasses.right;
  const transformClass = transformClasses[position] || transformClasses.right;

  // Create portal to render the panel directly to the document body
  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out ${
          isAnimating ? "bg-opacity-25 opacity-100" : "bg-opacity-0 opacity-0"
        }`}
        style={{ zIndex: 9998 }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`fixed top-0 ${positionClass} h-full bg-white shadow-lg transition-transform  duration-300 ease-in-out ${transformClass}`}
        style={{ width, zIndex: 9999 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="panel-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 id="panel-title" className="text-2xl fraunces font-bold text-gray-800">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Cerrar panel"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-56px)]">
          {React.isValidElement(contentToRender)
            ? React.cloneElement(contentToRender, contentProps)
            : contentToRender}
        </div>
      </div>
    </>,
    document.body
  );
};

export default MultiSidePanel;